import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone, type FileRejection } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CircularProgress from '@mui/material/CircularProgress';
import { ACCENT_COLOR } from '@/constants/colors';

interface SORUploadProps {
  /** Called with the accepted file. */
  onFileAccepted: (file: File) => void;
  /** Currently uploaded file name (or null). */
  fileName: string | null;
  /** Whether the upload is in progress. */
  loading?: boolean;
  /** Whether the upload zone is disabled (e.g. BYD mode). */
  disabled?: boolean;
  /** Called when the user clicks the clear (✕) button. */
  onClear?: () => void;
}

const ACCEPTED_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
};

/**
 * SORUpload — Drag-and-drop zone for SOR Excel files.
 *
 * Uses react-dropzone to accept .xlsx/.xls files, shows file name
 * after upload, and a spinner while parsing is in progress.
 */
const SORUpload: React.FC<SORUploadProps> = ({
  onFileAccepted,
  fileName,
  loading = false,
  disabled = false,
  onClear,
}) => {
  const [fileError, setFileError] = useState<string | null>(null);

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        setFileError('Unsupported file format.');
        return;
      }
      setFileError(null);
      if (accepted.length > 0) {
        onFileAccepted(accepted[0]);
      }
    },
    [onFileAccepted],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    disabled: disabled || loading,
    multiple: false,
  });

  return (
    <>
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        p: 1.5,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: isDragActive ? 'action.hover' : 'transparent',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': disabled
          ? {}
          : { borderColor: 'info.main', bgcolor: 'rgba(25, 118, 210, 0.04)' },
      }}
    >
      <input {...getInputProps()} />

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            Parsing SOR…
          </Typography>
        </Box>
      ) : fileName ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <InsertDriveFileIcon sx={{ fontSize: 18, color: ACCENT_COLOR }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: ACCENT_COLOR, wordBreak: 'break-all' }}
          >
            {fileName}
          </Typography>
          {onClear && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              sx={{ ml: 0.5, p: 0.25 }}
              aria-label="Remove uploaded SOR file"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <CloudUploadIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            {isDragActive
              ? 'Drop SOR file here'
              : 'Drag & drop SOR (.xlsx / .xls) or click'}
          </Typography>
        </Box>
      )}
    </Box>
    {fileError && (
      <Typography variant="caption" color="error" sx={{display: 'block' }}>
        {fileError}
      </Typography>
    )}
    </>
  );
};

export default React.memo(SORUpload);
